/**
 * Land facts read from a plot's own listing text by the backend
 * (services/land_facts.py): utilities, designation, fence, road, and so
 * on. Every key is optional; a missing key means the text said nothing.
 * Labels here are shared by the listing card and the detail panel.
 */
import type { TranslationKey } from "@/lib/i18n";

export type UtilityState = "yes" | "nearby" | "no";
export interface LandFacts {
  purpose?: "residential" | "construction" | "agricultural" | "commercial" | "industrial";
  utilities?: Partial<Record<"gas" | "water" | "electricity" | "sewage", UtilityState>>;
  road_access?: boolean;
  main_road_m?: number;
  sea_m?: number;
  fenced?: "yes" | "partial" | "no";
  structure?: boolean;
  trees?: boolean;
  terrain?: "flat" | "slope";
  dims_m?: [number, number];
  divisible?: boolean;
  municipal_doc?: boolean;
  v?: number;
}

type T = (k: TranslationKey) => string;
const UTILS = ["gas", "water", "electricity", "sewage"] as const;
const num = (n: number) => n.toLocaleString("ru-RU").replace(/\s/g, "\u00a0");

/** The plot's strong points for a card's quiet line: what is on site. */
export function landCardBits(f: LandFacts | null | undefined, t: T): string[] {
  if (!f) return [];
  const out: string[] = [];
  const onSite = UTILS.filter((u) => f.utilities?.[u] === "yes").map((u) => t(`lf.${u}` as TranslationKey));
  if (onSite.length) out.push(onSite.join(", "));
  if (f.fenced === "yes") out.push(t("lf.fenced.yes"));
  if (f.road_access) out.push(t("lf.road.access"));
  return out;
}

/** Label-value rows for the detail panel's facts ledger. */
export function landDetailRows(f: LandFacts | null | undefined, t: T): { label: string; value: string }[] {
  if (!f) return [];
  const rows: { label: string; value: string }[] = [];
  if (f.purpose) rows.push({ label: t("lf.purpose"), value: t(`lf.purpose.${f.purpose}` as TranslationKey) });
  const u = UTILS.filter((k) => f.utilities?.[k]).map((k) => `${t(`lf.${k}` as TranslationKey)}: ${t(`lf.u.${f.utilities![k]}` as TranslationKey)}`);
  if (u.length) rows.push({ label: t("lf.utilities"), value: u.join(", ") });
  const road = [f.road_access ? t("lf.road.access") : null, f.main_road_m != null ? t("lf.road.main").replace("{m}", num(f.main_road_m)) : null].filter(Boolean);
  if (road.length) rows.push({ label: t("lf.road"), value: road.join(", ") });
  if (f.sea_m != null) rows.push({ label: t("lf.sea"), value: t("lf.sea.v").replace("{m}", num(f.sea_m)) });
  if (f.fenced) rows.push({ label: t("lf.fenced"), value: t(`lf.fenced.${f.fenced}` as TranslationKey) });
  if (f.terrain) rows.push({ label: t("lf.terrain"), value: t(`lf.terrain.${f.terrain}` as TranslationKey) });
  if (f.dims_m) rows.push({ label: t("lf.dims"), value: `${num(f.dims_m[0])} × ${num(f.dims_m[1])} m` });
  const extra = [f.structure ? t("lf.structure") : null, f.trees ? t("lf.trees") : null, f.divisible ? t("lf.divisible") : null].filter(Boolean);
  if (extra.length) rows.push({ label: t("lf.on_plot"), value: extra.join(", ") });
  if (f.municipal_doc) rows.push({ label: t("lf.municipal"), value: t("lf.municipal.note") });
  return rows;
}
